import { messageForCode, resolveErrorCode } from './error-codes.js';
/** Codes the transport layer itself invents when there was no HTTP response. */
export const TRANSPORT_ERROR_CODES = {
    /** The request never reached the server (offline, DNS, TLS, blocked). */
    NETWORK: 'NETWORK',
    /** The request was aborted by the client's own timeout. */
    TIMEOUT: 'TIMEOUT',
    /** The caller aborted it — a superseded search, an unmounted screen. */
    CANCELLED: 'CANCELLED',
    /** A response arrived but was not shaped like anything we recognise. */
    MALFORMED: 'MALFORMED',
};
const TRANSPORT_CODE_SET = new Set(Object.values(TRANSPORT_ERROR_CODES));
export class ApiError extends Error {
    statusCode;
    code;
    details;
    requestId;
    /** The server's own sentence, before any fallback was applied. */
    serverMessage;
    constructor(body) {
        const code = resolveErrorCode(body.code, body.message);
        super(messageForCode(code, body.message));
        this.name = 'ApiError';
        this.statusCode = body.statusCode;
        this.code = code ?? 'UNKNOWN';
        this.details = body.details;
        this.requestId = body.requestId;
        this.serverMessage = body.message;
    }
    /**
     * Reads as a sentence in Persian — safe to put straight into a message box.
     *
     * A transport failure always uses our own copy: axios fills `message` with
     * `"Network Error"` or `"timeout of 30000ms exceeded"`, which is English and
     * meaningless to a clinic receptionist.
     */
    get faMessage() {
        if (TRANSPORT_CODE_SET.has(this.code))
            return messageForCode(this.code, undefined);
        return messageForCode(this.code, this.serverMessage);
    }
    /**
     * Field-level messages from `class-validator`, when the failure was a 400.
     *
     * The filter sends them either as a bare string array or wrapped in
     * `{ message: [...] }`, so this narrows both rather than casting.
     */
    get fieldMessages() {
        const d = this.details;
        if (Array.isArray(d) && d.every((x) => typeof x === 'string'))
            return d;
        if (d && typeof d === 'object' && Array.isArray(d.message)) {
            return d.message.filter((x) => typeof x === 'string');
        }
        return [];
    }
    /** The structured `[{ field, messages }]` form, when the filter sent that one. */
    get fieldErrors() {
        if (!Array.isArray(this.details))
            return [];
        return this.details.filter((entry) => {
            if (typeof entry !== 'object' || entry === null)
                return false;
            const candidate = entry;
            return typeof candidate.field === 'string' && Array.isArray(candidate.messages);
        });
    }
    get isUnauthorized() {
        return this.statusCode === 401;
    }
    get isForbidden() {
        return this.statusCode === 403;
    }
    get isNotFound() {
        return this.statusCode === 404;
    }
    get isRateLimited() {
        return this.statusCode === 429;
    }
    /** `true` for 401/403 — the two the auth layer reacts to. */
    get isAuthFailure() {
        return this.statusCode === 401 || this.statusCode === 403;
    }
    /**
     * `true` when the backend refused for want of a permission, not an identity.
     * A 2FA step-up is excluded: that one the user can actually resolve.
     */
    get isPermissionDenied() {
        return this.statusCode === 403 && this.code !== 'TWO_FACTOR_REQUIRED';
    }
    /**
     * Whether retrying the exact same request could plausibly succeed. Used by
     * the query client's `retry` predicate — retrying a 403 just burns the user's
     * time and fills the audit log.
     */
    get isRetryable() {
        if (this.code === TRANSPORT_ERROR_CODES.NETWORK || this.code === TRANSPORT_ERROR_CODES.TIMEOUT)
            return true;
        return this.statusCode >= 500;
    }
}
export function isApiError(value) {
    return value instanceof ApiError;
}
const PERSIAN_LETTER = /[\u0600-\u06FF]/;
const ERROR_CODE_SHAPE = /^[A-Z][A-Z0-9_]+$/;
/**
 * Best-effort Persian sentence for anything thrown anywhere.
 *
 * A plain `Error`'s message is shown only when it is already Persian. It used
 * to pass through whatever it said, so `new Error('PATIENT_NOT_FOUND')` put the
 * code itself under «بارگذاری انجام نشد», and a failed lazy chunk after a
 * deploy put «Failed to fetch dynamically imported module: https://…» on a
 * clinic's screen. A code-shaped message is looked up in the code table; any
 * other non-Persian text becomes the generic sentence.
 */
export function errorMessageFa(error) {
    if (isApiError(error))
        return error.faMessage;
    const text = error instanceof Error ? error.message.trim() : typeof error === 'string' ? error.trim() : '';
    if (text === '')
        return messageForCode('UNKNOWN');
    if (PERSIAN_LETTER.test(text))
        return text;
    if (ERROR_CODE_SHAPE.test(text))
        return messageForCode(text);
    return messageForCode('UNKNOWN');
}
//# sourceMappingURL=api-error.js.map